/**
 * Drag-state appearance, owned by the BUNDLE.
 *
 * <g-kanban>, <g-notes>, <g-task-list> and <g-tree> all hand SortableJS the same three
 * class names. Until now nothing in here defined what they look like — the CSS
 * lived in `boilerplate/public/style.css`, so every app carried its own copy of
 * a component's appearance.
 *
 * That is backwards, and it showed: correcting one rule meant patching 505
 * apps, publish does not re-sync the boilerplate shell so most of them would
 * never get it, and an app built before the rules existed had no drag styling
 * at all. A component whose look is defined outside itself is not shippable via
 * a CDN — which is the whole point of shipping it via a CDN.
 *
 * Injected at runtime, same as ensureStatusStyles(). That also fixes apps
 * already in the wild: a stale `.g-card-chosen { transform: rotate(2deg) }` in
 * an app stylesheet is a `<link>` in <head>, and this <style> is appended after
 * it. Equal specificity, later wins — so `transform: none` below overrides the
 * old rule without !important, in every existing build, with no backfill.
 */
const STYLE_ID = 'g-drag-styles';

/** The names every draggable component hands SortableJS. One source. */
export const DRAG_CLASSES = {
  ghostClass: 'g-card-ghost',
  chosenClass: 'g-card-chosen',
  dragClass: 'g-card-drag',
};

const DRAG_CSS = `
/* Where the item will land. */
.g-card-ghost {
  opacity: 0.4;
  background-color: color-mix(in oklch, var(--color-primary) 10%, transparent) !important;
  border: 2px dashed color-mix(in oklch, var(--color-primary) 50%, transparent) !important;
}

/* The element you grabbed. NEVER transform this — Sortable MEASURES it to size
 * the carried copy, and a rotation inflates the bounding box by the element's
 * own width. On a 1942px table row, rotate(2deg) added 68px, so a 37px row was
 * carried as a 107px slab. 'none' is stated explicitly to beat the stale rule
 * still sitting in older apps' stylesheets. */
.g-card-chosen {
  box-shadow: 0 8px 16px color-mix(in oklch, var(--color-base-content) 15%, transparent) !important;
  transform: none;
}

/* The copy under the cursor: 'dragClass' on the native path,
 * 'sortable-fallback' on the pointer-driven one. Never measured, so it is free
 * to tilt. */
.g-card-drag,
.sortable-fallback {
  opacity: 1 !important;
  box-shadow: 0 12px 24px color-mix(in oklch, var(--color-base-content) 20%, transparent) !important;
  transform: rotate(3deg) scale(1.02);
}

/* A full-width row tilts badly and reads as broken; cards keep the flourish. */
g-task-item.g-card-drag,
g-task-item.sortable-fallback,
.g-tree-drag-clone {
  transform: none;
}
`;

export function ensureDragStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = DRAG_CSS;
  document.head.appendChild(style);
}
